import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { getMcpStatus } from "./mcpEndpoints";
import type { McpStatusResponse, McpStatusScope } from "./mcp.types";

export type McpStatusResult =
  | { available: true; status: McpStatusResponse; fellBackToMe: boolean }
  /** The MCP status route does not exist in this environment (404). */
  | { available: false };

const httpStatusOf = (error: unknown) => (isAxiosError(error) ? error.response?.status : undefined);

const fetchMcpStatus = async (workspaceId: string, scope: McpStatusScope): Promise<McpStatusResult> => {
  try {
    const res = await getMcpStatus({ workspace_id: workspaceId, scope });
    return { available: true, status: res.data, fellBackToMe: false };
  } catch (error) {
    const code = httpStatusOf(error);
    if (code === 404) return { available: false };
    if (code === 403 && scope === "workspace") {
      const res = await getMcpStatus({ workspace_id: workspaceId, scope: "me" });
      return { available: true, status: res.data, fellBackToMe: true };
    }
    throw error;
  }
};

export const useMcpStatus = ({
  workspaceId,
  scope = "me",
  enabled = true,
}: {
  workspaceId?: string;
  scope?: McpStatusScope;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: ["mcpStatus", workspaceId, scope],
    queryFn: () => fetchMcpStatus(workspaceId as string, scope),
    enabled: Boolean(workspaceId) && enabled,
    keepPreviousData: true,
    retry: (failureCount, error) => {
      const code = httpStatusOf(error);
      if (code && code >= 400 && code < 500) return false;
      return failureCount < 2;
    },
  });
